import {ApiContext, type fetchOptions} from "./ApiContext.tsx";
import {useAuth} from "../hooks/useAuth.tsx";
import type {IApiResponse} from "../sitemap/apiendponts.ts";
import {useRef, useState, useCallback, ReactNode} from "react";
import {toast} from "react-toastify";

const MAX_HTTP_REQUESTS = 8

export function ApiProvider({children}: { children: ReactNode }) {
	const {token, isAuthenticated} = useAuth()
	const [pendingRequestCount, setPendingRequestCount] = useState(0)
	const pendingRequests = useRef<Map<string, Promise<any>>>(new Map())
	const completedRequests = useRef<Map<string, { data: any, timestamp: number }>>(new Map())

	const activeHttpRequests = useRef<number>(0)
	const requestQueue = useRef<(() => void)[]>([])

	const processQueue = useCallback(() => {
		while (activeHttpRequests.current < MAX_HTTP_REQUESTS && requestQueue.current.length > 0) {
			const nextRequest = requestQueue.current.shift()
			if (nextRequest) {
				nextRequest()
			}
		}
	}, [])

	const enqueueRequest = useCallback(<T, >(fn: () => Promise<T>, signal?: AbortSignal): Promise<T> => {
		return new Promise<T>((resolve, reject) => {
			let run: (() => void) | null = null

			const onAbort = () => {
				reject(new DOMException('Aborted', 'AbortError'))
				if (run) {
					const index = requestQueue.current.indexOf(run)
					if (index !== -1) {
						requestQueue.current.splice(index, 1)
					}
				}
				processQueue()
			}

			if (signal?.aborted) {
				reject(new DOMException('Aborted', 'AbortError'))
				return
			}

			if (signal) {
				signal.addEventListener('abort', onAbort)
			}

			run = async () => {
				if (signal?.aborted) {
					reject(new DOMException('Aborted', 'AbortError'))
					processQueue()
					return
				}

				if (signal) {
					signal.removeEventListener('abort', onAbort)
				}

				activeHttpRequests.current++
				try {
					const result = await fn()
					resolve(result)
				} catch (error) {
					reject(error)
				} finally {
					activeHttpRequests.current--
					processQueue()
				}
			}

			requestQueue.current.push(run)
			processQueue()
		})
	}, [processQueue])

	const buildQueryString = (data: object) => {
		return Object.entries(data ?? {}).filter(([_key, value]) => value !== null && value !== undefined && value !== '').flatMap(([key, value]) => {
			if (Array.isArray(value)) {
				return value.map(v => `${key}=${v}`)
			}
			return [`${key}=${value}`]
		}).join('&')
	}

	async function fetchData<T>(options: fetchOptions<T>, ignoreCache?: boolean) {
		const {endpoint, pagination, query} = options

		const {page, pageLimit} = pagination ?? {page: undefined, pageLimit: undefined}

		let url = '/Proxy' + options.endpoint.url
		const qs = buildQueryString({...query, ...endpoint.query, page: page, pageLimit: pageLimit})
		url = qs.length > 0 ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url

		const requestKey = JSON.stringify({
			url,
			method: endpoint.method,
			body: endpoint.body ?? {}
		})

		const isGet = endpoint.method === 'GET'
		const useCache = isGet && !(pagination?.ignoreCache || ignoreCache)

		if (useCache) {
			const completed = completedRequests.current.get(requestKey)

			if (completed && (Date.now() - completed.timestamp < 2000)) {
				// console.log(`[API] Reusing recently completed request (cache 2s) for: ${url}`)
				return completed.data as IApiResponse<T>
			}
		}

		const cached = pendingRequests.current.get(requestKey)
		if (cached) {
			// console.log(`[API] Reusing pending request for: ${url}`)
			return cached as Promise<IApiResponse<T>>
		}

		// console.log(`[API] Starting new request: ${url}`)
		setPendingRequestCount(prev => prev + 1)

		let headers: RequestInit['headers'] = {
			'Content-Type': 'application/json'
		}

		if (isAuthenticated) {
			headers = {...headers, Authorization: `Bearer ${token ?? ''}`}
		}

		if (pagination?.ignoreCache || ignoreCache) {
			headers = {...headers, 'Cache-Control': 'no-cache'}
		}

		const init: RequestInit = {
			method: endpoint.method,
			headers,
			body: JSON.stringify(endpoint.body),
			signal: options.signal
		}

		const promise = enqueueRequest(async () => {
			const x = await fetch(url, init);
			const x_1 = await x.json();
			const x_2 = x_1 as IApiResponse<T>;
			if (x_2.message != null && (!x_2.success && (x_2.message.important || ['error'].includes(x_2.message.severity)))) {
				toast(x_2.message.message, {
					type: x_2.message.severity,
				});
			}
			const minorMessages = x_2.minorMessages?.filter(m => (m.important || ['error'].includes(m.severity))) ?? [];
			minorMessages.forEach(m_1 => {
				toast(m_1.message, {
					type: m_1.severity,
				});
			});
			if (useCache) {
				const timestamp = Date.now();
				completedRequests.current.set(requestKey, {data: x_2, timestamp});
				setTimeout(() => {
					const current = completedRequests.current.get(requestKey);
					if (current && current.timestamp === timestamp) {
						completedRequests.current.delete(requestKey);
					}
				}, 3000);
			}
			return x_2;
		}, options.signal)
			.then(async (x) => {
				return x
			})
			.finally(() => {
				pendingRequests.current.delete(requestKey)
				// console.log(`[API] Cleaning up pending request for: ${url}`)
				setPendingRequestCount(prev => prev - 1)
			})

		pendingRequests.current.set(requestKey, promise)

		return promise
	}

	return (<ApiContext.Provider value={{fetchData}}>
		{pendingRequestCount > 0 && <div style={{
			position: 'fixed',
			top: 10,
			right: 10,
			background: 'rgba(0,0,0,0.5)',
			color: 'white',
			padding: '5px 10px',
			borderRadius: '5px',
			zIndex: 9999,
			fontSize: '12px'
		}}>
			Carregando {pendingRequestCount} requisições...
		</div>}
		{children}
	</ApiContext.Provider>)
}